"use client";
import type { GridState } from "../../types/GridStates";
import { getGridStateLabel } from "../../types/GridStates";
import { Badge, type BadgeProps } from "../Badge";
import { Icon } from "../Icon";
import { Skeleton } from "../Skeleton";

export interface GridStateBadgeProps extends Omit<BadgeProps, "variant" | "children"> {
  /** The grid state to display */
  state: GridState;
  /** Custom label override (defaults to auto-capitalized state name) */
  label?: string;
  /** Show only directional icon without text (arrow up for exporting, arrow down for importing) */
  showIcon?: boolean;
  /** Loading state */
  isLoading?: boolean;
  /** Fixed width for column alignment (useful in tables) */
  fixedWidth?: boolean;
}

/**
 * GridStateBadge
 *
 * A specialized badge component for displaying grid states (importing, exporting).
 * Optionally shows directional icons to indicate power flow direction.
 * Uses semantic grid state colors from the theme.
 *
 * @example
 * ```tsx
 * <GridStateBadge state="importing" />
 * <GridStateBadge state="exporting" showIcon />
 * <GridStateBadge state="importing" label="Grid Import" />
 * ```
 */
export function GridStateBadge({
  state,
  label,
  showIcon = false,
  isLoading,
  size = "sm",
  shape = "pill",
  fixedWidth,
  className,
  ...badgeProps
}: GridStateBadgeProps) {
  if (isLoading) {
    return <Skeleton className="inline-flex h-6 w-24" />;
  }

  const displayLabel = label ?? getGridStateLabel(state);
  const icon = state === "exporting" ? "ArrowUp" : "ArrowDown";
  const iconSize = size === "sm" ? 12 : size === "md" ? 14 : 16;

  // When showIcon is true, show only the icon without text (compact mode)
  if (showIcon) {
    return (
      <Badge
        variant={state}
        size={size}
        shape={shape}
        className={`w-6 justify-center ${className || ""}`}
        {...badgeProps}
      >
        <Icon name={icon} size={iconSize} />
      </Badge>
    );
  }

  // Default: show label with optional icon
  return (
    <Badge
      variant={state}
      size={size}
      shape={shape}
      className={fixedWidth ? `w-28 justify-center ${className || ""}` : className}
      {...badgeProps}
    >
      {displayLabel}
    </Badge>
  );
}
